import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { api, query } from '../lib/api/client';
import type { Produto, TerritorioDoProduto } from '../lib/api/types';
import { useAuth } from '../lib/auth/AuthContext';
import { PageHeader } from '../components/ui/PageHeader';
import { Carregando, Erro, Vazio } from '../components/ui/Estados';
import { MapaDoBrasil } from '../components/mapa/MapaDoBrasil';
import { territorioEmTexto } from '../components/territorio/EditorDeTerritorio';
import { emData } from '../lib/format/datetime';

/**
 * O painel de indicadores (A21).
 *
 * <h3>Seis números, e cada um precisa fazer alguém agir</h3>
 * Um dashboard com vinte números é ignorado — e um número ignorado é pior do que ausente,
 * porque ocupa o lugar onde alguém procuraria o que importa.
 *
 * <h3>Suspeita de clone só aparece para quem tem etiqueta eletrônica</h3>
 * O servidor <b>omite</b> o campo para a linha Origem, e a tela não o inventa. Mostrar
 * "suspeitas de clone: 0" para um cliente de QR ensinaria que o número existe — e no dia em
 * que ele fosse 1, por um parâmetro sujo numa URL pública, haveria um incidente sobre uma
 * promessa que o QR nunca fez.
 *
 * <h3>O funil é o gráfico, não os números</h3>
 * Os seis dizem <i>quanto</i>. O funil diz <i>onde</i> se perde — e o maior degrau é sempre a
 * próxima coisa a resolver. Nenhuma verificação criptográfica revela um embarque que ninguém
 * escaneou; este é o único instrumento que temos sobre isso.
 */

interface Numeros {
  itensGerados: number;
  itensAtivos: number;
  taxaDeAtivacao: number | null;
  leiturasDeOperador: number;
  leiturasDeConsumidor: number;
  suspeitasDeClone: number | null;
  anomaliasAbertas: number;
  anomaliaMaisAntigaEmDias: number | null;
  sessoesPendentesDeAncoragem: number;
  lagDeAncoragemP95EmSegundos: number | null;
  diasDoPeriodo: number;
}

interface Degrau {
  passo: string;
  itens: number;
  proporcao: number | null;
}

interface MapaPorRegiao {
  ufs: { uf: string; leituras: number }[];
  ufsSuprimidas: number;
  leiturasEmUfsSuprimidas: number;
  cidades: { cidade: string; uf: string; leituras: number }[];
  cidadesSuprimidas: number;
  leiturasEmCidadesSuprimidas: number;
  exterior: { pais: string; leituras: number }[];
  leiturasNoExterior: number;
  semLocalizacao: number;
  total: number;
  limiar: number;
  dias: number;
  atualizadoAte?: string;
  /** O território declarado do produto filtrado (ADR-128). */
  territorio?: string[];
}

export function DashboardPage() {
  const { traduzir, temPapel } = useAuth();
  const podeVerOnde = temPapel('COMPANY_ADMIN', 'COMPANY_SECURITY', 'COMPANY_ANALYST');
  const [diasDoMapa, setDiasDoMapa] = useState(90);
  const [produtoDoMapa, setProdutoDoMapa] = useState('');

  const numeros = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<Numeros>('/console/dashboard?days=30'),
  });
  const funil = useQuery({
    queryKey: ['dashboard', 'funil'],
    queryFn: () => api.get<Degrau[]>('/console/dashboard/funnel'),
  });
  const mapa = useQuery({
    queryKey: ['dashboard', 'regioes', diasDoMapa, produtoDoMapa],
    queryFn: () =>
      api.get<MapaPorRegiao>(
        `/console/dashboard/regions${query({ days: diasDoMapa, productId: produtoDoMapa })}`,
      ),
    enabled: podeVerOnde,
  });
  const produtos = useQuery({
    queryKey: ['produtos'],
    queryFn: () => api.get<Produto[]>('/console/products'),
    enabled: podeVerOnde,
  });

  if (numeros.isPending) {
    return <Carregando o="os indicadores" />;
  }
  if (numeros.isError) {
    return <Erro erro={numeros.error} />;
  }
  const n = numeros.data;

  return (
    <>
      <PageHeader
        titulo="Indicadores"
        subtitulo={`Os últimos ${n.diasDoPeriodo} dias. Cada número aqui existe para fazer alguém agir.`}
      />
      <div className="wrap">
        <div className="grade">
          <Numero
            rotulo="Itens ativos"
            valor={n.itensAtivos}
            nota={
              // == e não ===: o servidor OMITE o campo nulo (inclusão non_null), então
              // ele chega como undefined, e a comparação estrita deixaria passar um
              // 'undefined' formatado dentro da frase.
              n.taxaDeAtivacao == null
                ? 'Nenhum serial gerado ainda'
                : `${pct(n.taxaDeAtivacao)} dos ${n.itensGerados} gerados. A diferença é etiqueta parada — ou vazada`
            }
          />
          <Numero
            rotulo="Leituras de operador"
            valor={n.leiturasDeOperador}
            nota="O volume da operação no período"
          />
          <Numero
            rotulo="Leituras de consumidor"
            valor={n.leiturasDeConsumidor}
            nota="Alcance junto ao público. Fora da cadeia, nunca ancorado"
          />
          {n.suspeitasDeClone != null && (
            <Numero
              rotulo="Suspeitas de clone"
              valor={n.suspeitasDeClone}
              alarme={n.suspeitasDeClone > 0}
              nota="Qualquer valor acima de zero exige apuração"
            />
          )}
          <Numero
            rotulo="Alertas abertos"
            valor={n.anomaliasAbertas}
            alarme={n.anomaliasAbertas > 0}
            nota={
              n.anomaliaMaisAntigaEmDias == null
                ? 'Nada na fila'
                : `O mais antigo tem ${n.anomaliaMaisAntigaEmDias} dia(s). Fila que envelhece vira dado sem valor`
            }
          />
          <Numero
            rotulo="Sessões sem raiz"
            valor={n.sessoesPendentesDeAncoragem}
            alarme={n.sessoesPendentesDeAncoragem > 0}
            nota={
              n.lagDeAncoragemP95EmSegundos == null
                ? 'Nenhuma raiz calculada ainda'
                : `Da sessão fechada à raiz, 95% em até ${duracao(n.lagDeAncoragemP95EmSegundos)}`
            }
          />
        </div>

        <div className="card cd" style={{ marginTop: 24 }}>
          <div className="cd-t">Funil da cadeia</div>
          <div className="cd-s">
            Quantos itens alcançaram cada passo. O maior degrau é a próxima coisa a resolver —
            nenhuma prova criptográfica revela um embarque que ninguém escaneou.
          </div>

          {funil.isPending && <Carregando />}
          {funil.isError && <Erro erro={funil.error} />}
          {funil.data && funil.data.length === 0 && (
            <Vazio titulo="Nenhuma leitura ainda" detalhe="O funil aparece com o primeiro evento." />
          )}
          {funil.data && funil.data.length > 0 && (
            <table className="t" style={{ marginTop: 16 }}>
              <tbody>
                {funil.data.map((d, i) => (
                  <tr key={d.passo}>
                    <td style={{ width: 180, fontWeight: 600 }}>{traduzir('bizStep', d.passo)}</td>
                    <td>
                      <div
                        style={{
                          height: 18,
                          borderRadius: 4,
                          background: 'var(--teal)',
                          opacity: 0.85,
                          width: `${Math.max((d.proporcao ?? 0) * 100, 1)}%`,
                        }}
                      />
                    </td>
                    <td className="num" style={{ width: 90, textAlign: 'right' }}>
                      {d.itens}
                    </td>
                    <td className="num t-cap" style={{ width: 70, textAlign: 'right' }}>
                      {d.proporcao == null ? '—' : pct(d.proporcao)}
                    </td>
                    <td className="t-cap" style={{ width: 90 }}>
                      {i > 0 && degrau(funil.data[i - 1]!.itens, d.itens)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {podeVerOnde && (
          <div className="card cd" style={{ marginTop: 24 }}>
            <div className="cd-t">Onde leram</div>
            <div className="cd-s">
              Leituras de consumidor por UF e cidade, derivadas do IP — <strong>localização
              aproximada</strong>: a UF costuma acertar, a cidade aponta para a operadora.
            </div>

            <div className="filtros" style={{ marginTop: 14 }}>
              <div className="campo">
                <label htmlFor="mapa-dias">Período</label>
                <select
                  id="mapa-dias"
                  value={String(diasDoMapa)}
                  onChange={(e) => setDiasDoMapa(Number(e.target.value))}
                >
                  <option value="30">Últimos 30 dias</option>
                  <option value="90">Últimos 90 dias</option>
                  <option value="365">Últimos 12 meses</option>
                  <option value="3650">Todo o histórico</option>
                </select>
              </div>
              <div className="campo">
                <label htmlFor="mapa-produto">Produto</label>
                <select
                  id="mapa-produto"
                  value={produtoDoMapa}
                  onChange={(e) => setProdutoDoMapa(e.target.value)}
                >
                  <option value="">Todos</option>
                  {(produtos.data ?? []).map((p) => (
                    <option key={p.id} value={p.id}>
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {mapa.isPending && <Carregando o="o mapa" />}
            {mapa.isError && <Erro erro={mapa.error} />}
            {mapa.data && mapa.data.total === 0 && (
              <Vazio
                titulo="Nenhuma leitura de consumidor no período"
                detalhe="O mapa é calculado de madrugada: as leituras de hoje entram amanhã."
              />
            )}
            {mapa.data && mapa.data.total > 0 && <OndeLeram m={mapa.data} />}
            {produtoDoMapa && <LotesComTerritorioProprio productId={produtoDoMapa} />}
          </div>
        )}
      </div>
    </>
  );
}

/**
 * O mapa, o ranking e o que ficou de fora — nesta ordem de leitura.
 *
 * <p>O que foi suprimido aparece em número, sempre: um mapa que esconde sem avisar faz concluir que
 * ninguém leu naquela região, e é justamente leitura onde não se esperava que interessa (ADR-080).
 */
function OndeLeram({ m }: { m: MapaPorRegiao }) {
  const porUf: Record<string, number> = {};
  m.ufs.forEach((u) => {
    porUf[u.uf] = u.leituras;
  });
  const noBrasil = m.ufs.reduce((s, u) => s + u.leituras, 0) + m.leiturasEmUfsSuprimidas;

  return (
    <>
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))',
          gap: 24,
          marginTop: 16,
          alignItems: 'start',
        }}
      >
        <MapaDoBrasil leituras={porUf} territorio={m.territorio} />

        <div>
          <table className="t">
            <thead>
              <tr>
                <th>UF</th>
                <th style={{ textAlign: 'right' }}>Leituras</th>
              </tr>
            </thead>
            <tbody>
              {m.ufs.map((u) => (
                <tr key={u.uf}>
                  <td>{u.uf}</td>
                  <td className="num" style={{ textAlign: 'right' }}>
                    {u.leituras}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div>
          {m.cidades.length === 0 ? (
            <div className="t-small">
              Nenhuma cidade com {m.limiar} leituras ou mais no período.
            </div>
          ) : (
            <table className="t">
              <thead>
                <tr>
                  <th>Cidade (aprox.)</th>
                  <th style={{ textAlign: 'right' }}>Leituras</th>
                </tr>
              </thead>
              <tbody>
                {m.cidades.slice(0, 15).map((c) => (
                  <tr key={`${c.uf}-${c.cidade}`}>
                    <td>
                      {c.cidade}/{c.uf}
                    </td>
                    <td className="num" style={{ textAlign: 'right' }}>
                      {c.leituras}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      <div className="t-small" style={{ marginTop: 16, lineHeight: 1.6 }}>
        <strong>{m.total}</strong> leitura(s) no período: {noBrasil} no Brasil,{' '}
        {m.leiturasNoExterior} no exterior
        {m.exterior.length > 0 &&
          ` (${m.exterior.map((p) => `${p.pais} ${p.leituras}`).join(', ')})`}
        , {m.semLocalizacao} sem localização.
      </div>

      {m.territorio && m.territorio.length > 0 && (
        <ForaDoTerritorio m={m} territorio={m.territorio} />
      )}

      {(m.ufsSuprimidas > 0 || m.cidadesSuprimidas > 0) && (
        <div className="aviso" style={{ marginTop: 12 }}>
          {m.ufsSuprimidas > 0 && (
            <>
              <strong>{m.ufsSuprimidas} UF(s)</strong> com menos de {m.limiar} leituras (
              {m.leiturasEmUfsSuprimidas} no total) ficaram sem cor.{' '}
            </>
          )}
          {m.cidadesSuprimidas > 0 && (
            <>
              <strong>{m.cidadesSuprimidas} cidade(s)</strong> com menos de {m.limiar} leituras (
              {m.leiturasEmCidadesSuprimidas} no total) não aparecem separadas.{' '}
            </>
          )}
          Poucas leituras numa cidade pequena identificam uma pessoa; elas continuam no total.
        </div>
      )}

      <div className="t-cap" style={{ marginTop: 12 }}>
        {m.atualizadoAte ? `Atualizado até ${emData(m.atualizadoAte)}.` : ''} As leituras de hoje entram
        amanhã. Cidade guardada desde 15/09/2026; antes disso, as leituras contam em "sem
        localização". O território declarado de distribuição — que transforma leitura longe em alerta
        de mercado paralelo — ainda não existe.
      </div>
    </>
  );
}

/**
 * Os lotes que têm território próprio (ADR-129).
 *
 * <p>O mapa é desenhado do total diário, que não sabe de que lote veio cada leitura — então ele
 * mostra o território do PRODUTO. Esconder que um lote foi para outro lugar faria as UFs dele
 * parecerem desvio; o aviso diz o que o desenho não diz, e que o alerta já leva isso em conta.
 */
function LotesComTerritorioProprio({ productId }: { productId: string }) {
  const territorio = useQuery({
    queryKey: ['produto', productId, 'territorio'],
    queryFn: () => api.get<TerritorioDoProduto>(`/console/products/${productId}/territory`),
  });
  const proprios = (territorio.data?.lotes ?? []).filter((l) => l.codes && l.codes.length > 0);
  if (proprios.length === 0) {
    return null;
  }
  return (
    <div className="t-small" style={{ marginTop: 10 }}>
      {proprios.map((l) => (
        <div key={l.batchId}>
          Lote <span className="mono-s">{l.code}</span> tem território próprio:{' '}
          <strong>{territorioEmTexto(l.codes)}</strong>.
        </div>
      ))}
      <div className="t-cap">
        O alerta já considera o território de cada lote; o desenho do mapa mostra o do produto.
      </div>
    </div>
  );
}

/**
 * A frase do território sob o mapa (ADR-128). Conta o que está visível — UF e país acima do limiar;
 * o alerta de verdade é do servidor, em janela de sete dias, e chega na fila de segurança.
 */
function ForaDoTerritorio({ m, territorio }: { m: MapaPorRegiao; territorio: string[] }) {
  const dentro = (uf: string) => territorio.includes('BR') || territorio.includes(`BR-${uf}`);
  const ufsFora = m.ufs.filter((u) => !dentro(u.uf));
  const paisesFora = m.exterior.filter((p) => !territorio.includes(p.pais));
  const leiturasFora =
    ufsFora.reduce((s, u) => s + u.leituras, 0) + paisesFora.reduce((s, p) => s + p.leituras, 0);

  return (
    <div className={leiturasFora > 0 ? 'aviso' : 't-small'} style={{ marginTop: 12 }}>
      Território declarado: <strong>{territorioEmTexto(territorio)}</strong>.{' '}
      {leiturasFora > 0 ? (
        <>
          <strong>{leiturasFora} leitura(s)</strong> fora dele no período (
          {[...ufsFora.map((u) => `${u.uf} ${u.leituras}`), ...paisesFora.map((p) => `${p.pais} ${p.leituras}`)].join(', ')}
          ), em âmbar no mapa.
        </>
      ) : (
        'Nenhuma UF ou país acima do limiar fora dele no período.'
      )}
    </div>
  );
}

function Numero({
  rotulo,
  valor,
  nota,
  alarme,
}: {
  rotulo: string;
  valor: number;
  nota: string;
  alarme?: boolean;
}) {
  return (
    <div className="card cd">
      <div className="cd2-t">{rotulo}</div>
      <div
        className="num"
        style={{
          fontSize: 34,
          fontWeight: 700,
          lineHeight: 1.1,
          color: alarme ? 'var(--danger)' : 'var(--navy)',
        }}
      >
        {valor}
      </div>
      <div className="t-cap" style={{ marginTop: 8 }}>
        {nota}
      </div>
    </div>
  );
}

function pct(v: number): string {
  return `${(v * 100).toFixed(1).replace('.', ',')}%`;
}

/** O tamanho do degrau — é ele que aponta onde a cadeia vaza. */
function degrau(anterior: number, atual: number): string {
  const perda = anterior - atual;
  if (perda <= 0) {
    return '';
  }
  return `−${perda}`;
}

function duracao(segundos: number): string {
  if (segundos < 90) {
    return `${segundos} s`;
  }
  if (segundos < 5400) {
    return `${Math.round(segundos / 60)} min`;
  }
  return `${Math.round(segundos / 3600)} h`;
}
